import { useCallback, useEffect, useState } from 'react'
import { FiEdit2 } from 'react-icons/fi'

import { Button } from '~/components/Button'
import { PageNavigation } from '~/components/PageNavigation'
import {
  deleteLicenseExtension,
  getLicenseExtensions,
} from '~/modules/sales/licenseExtensions/services'
import { LicenseExtension } from '~/modules/sales/licenseExtensions/types'
import { numberToBRL } from '~/modules/sales/services'
import { getLocalDate } from '~/utils/getLocalDate'

import { useLicenses } from '../../Licenses/context'
import { CreateEditLicenseExtensionModal } from '../components/LicenseExtensions/CreateEditLicenseExtensionModal'
import { DeleteLicenseExtensionButton } from '../components/LicenseExtensions/DeleteLicenseExtensionButton'

type FilterQuery = {
  page: number
  size: number
}

export const LicenseExtensions: React.FC = () => {
  const { refresh } = useLicenses()
  const [extensions, setExtensions] = useState<LicenseExtension[]>([])
  const [totalItems, setTotalItems] = useState(0)
  const [filterQuery, setFilterQuery] = useState<FilterQuery>({ page: 0, size: 10 })
  const [editTarget, setEditTarget] = useState<LicenseExtension | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)

  const fetchExtensions = useCallback((signal?: AbortSignal) => {
    getLicenseExtensions(filterQuery, signal).then((data) => {
      if (!data) return
      setExtensions(data.data)
      setTotalItems(data.paging.total)
    })
  }, [filterQuery])

  useEffect(() => {
    const controller = new AbortController()
    fetchExtensions(controller.signal)
    return () => {
      controller.abort()
    }
  }, [filterQuery, fetchExtensions])

  const handleUpdatePage = useCallback((page: number) => {
    setFilterQuery((s) => ({ ...s, page }))
  }, [])

  const handleDelete = useCallback(
    async (id: string) => {
      await deleteLicenseExtension(id)
      fetchExtensions()
      refresh()
    },
    [fetchExtensions, refresh],
  )

  return (
    <div className='flex flex-col flex-1 gap-4 m-5 p-5 bg-white rounded-xl overflow-hidden'>
      <div className='flex items-center justify-between'>
        <p className='font-medium'>Pacotes de licenças avulsas</p>
        <Button onClick={() => setShowCreateModal(true)}>
          + Novo pacote
        </Button>
      </div>

      <div className='max-w-full flex-1 overflow-hidden overflow-x-auto'>
        <table className='w-full min-w-[700px]'>
          <thead>
            <tr className='text-left'>
              <th>Nome</th>
              <th>Licenças</th>
              <th>Preço (ciclo cheio)</th>
              <th>Compras</th>
              <th>Status</th>
              <th>Criado</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {extensions.map((ext) => (
              <tr key={ext.id} className='even:bg-gpa-blue-50'>
                <td className='py-2 font-medium'>{ext.name}</td>
                <td className='py-2'>{ext.licensesAmount}</td>
                <td className='py-2'>{numberToBRL(ext.price / 100)}</td>
                <td className='py-2'>{ext._count?.purchases ?? 0}</td>
                <td className='py-2'>
                  <span
                    data-active={ext.active}
                    className='px-2 py-1 rounded-md font-bold text-sm
                      data-[active=true]:bg-green-100 data-[active=true]:text-green-700
                      data-[active=false]:bg-slate-100 data-[active=false]:text-slate-500'
                  >
                    {ext.active ? 'Ativo' : 'Inativo'}
                  </span>
                </td>
                <td className='py-2'>{getLocalDate(ext.createdAt)}</td>
                <td className='flex gap-2 py-2'>
                  <button
                    className='py-2 px-4 rounded-md bg-gpa-blue-500 hover:bg-blue-800 text-white'
                    onClick={() => setEditTarget(ext)}
                  >
                    <FiEdit2 size={18} />
                  </button>
                  <DeleteLicenseExtensionButton
                    extension={ext}
                    onDelete={() => handleDelete(ext.id)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className='self-end'>
        <PageNavigation
          numPages={Math.ceil(totalItems / filterQuery.size)}
          currentPage={filterQuery.page}
          onPageChange={handleUpdatePage}
        />
      </div>

      <CreateEditLicenseExtensionModal
        visible={showCreateModal || editTarget !== null}
        extension={editTarget}
        onSuccess={() => {
          setShowCreateModal(false)
          setEditTarget(null)
          fetchExtensions()
          refresh()
        }}
        onDismiss={() => {
          setShowCreateModal(false)
          setEditTarget(null)
        }}
      />
    </div>
  )
}
